import React from 'react';

import { MenuItem } from 'components';

import { dayjs, i18n, useTranslation } from 'third-party';

import { DISPLAY_DATE_TIME_FORMAT } from 'constants/common';

import { ListFilter } from 'components/Table/Filtering/ListFilter';
import { ColumnFilteringProps, TableColumn } from 'components/Table/types';

import { getEnumValues } from 'utils/helper';

import { APIAudit, APIUser } from 'types/api';
import { APIEntityType, APIEventType } from 'types/swaggerApi';

export const EventTypeFilter: React.FC<ColumnFilteringProps> = props => {
  const { t } = useTranslation();
  return (
    <ListFilter {...props}>
      <MenuItem key="all" value={undefined}>
        {t('common.all')}
      </MenuItem>
      {getEnumValues(APIEventType).map(status => (
        <MenuItem key={status} value={status}>
          {t(`audit.status.${status}`)}
        </MenuItem>
      ))}
    </ListFilter>
  );
};

export const EntityTypeFilter: React.FC<ColumnFilteringProps> = props => {
  const { t } = useTranslation();
  return (
    <ListFilter {...props}>
      <MenuItem key="all" value={undefined}>
        {t('common.all')}
      </MenuItem>
      {getEnumValues(APIEntityType).map(status => (
        <MenuItem key={status} value={status}>
          {status}
        </MenuItem>
      ))}
    </ListFilter>
  );
};

export const auditColumns: TableColumn<APIAudit>[] = [
  {
    dataId: 'eventType',
    label: i18n.t('auditList.column.eventType'),
    minWidth: 120,
    width: 140,
    hidden: false,
    sortable: true,
    resizable: true,
    customFilter: EventTypeFilter,
    formatter: (value: any) => ({
      value: String(i18n.t('audit.status.' + value)),
    }),
  },
  {
    dataId: 'data',
    label: i18n.t('auditList.column.description'),
    minWidth: 120,
    width: 400,
    hidden: false,
    sortable: true,
    notFilterable: true,
    resizable: true,
    formatter: (value: string[], item: APIAudit) => {
      if (item.entityType == APIEntityType.Payee) {
        return {
          value: String(
            i18n.t('auditList.payeeDescription', {
              name: value?.[0] || '',
              email: value?.[1] || '',
              phone: value?.[2] || '',
              status: value?.[3] || '',
            }),
          ),
        };
      }

      if (item.entityType == APIEntityType.Payment) {
        return {
          value: String(
            i18n.t('auditList.paymentDescription', {
              amount: value?.[0] || '',
              payee: value?.[1] || '',
              status: value?.[2] || '',
            }),
          ),
        };
      }

      if (item.entityType == APIEntityType.Bank) {
        return {
          value: String(
            i18n.t('auditList.bankDescription', {
              bankName: value?.[0] || '',
              accountName: value?.[1] || '',
              routing: value?.[2] || '',
              account: value?.[3] || '',
              companyName: value?.[4] || '',
            }),
          ),
        };
      }

      if (item.entityType == APIEntityType.User) {
        return {
          value: String(
            i18n.t('auditList.userDescription', {
              firstName: value?.[0] || '',
              lastName: value?.[1] || '',
              email: value?.[2] || '',
            }),
          ),
        };
      }

      if (item.entityType == APIEntityType.Settings) {
        return {
          value: String(
            i18n.t('auditList.settingsDescription', {
              name: i18n.t('settings.' + value?.[0]) || '',
            }),
          ),
        };
      }

      return {
        value: Array.isArray(value) ? value.join(', ') : String(value),
      };
    },
  },
  {
    dataId: 'entityType',
    label: i18n.t('auditList.column.entityType'),
    minWidth: 120,
    width: 140,
    hidden: false,
    sortable: true,
    resizable: true,
    customFilter: EntityTypeFilter,
  },
  {
    dataId: 'entityId',
    label: i18n.t('auditList.column.entityId'),
    minWidth: 120,
    width: 200,
    hidden: true,
    sortable: true,
    resizable: true,
    filterType: 'string',
  },
  {
    dataId: 'createdAt',
    label: i18n.t('auditList.column.createdAt'),
    minWidth: 120,
    width: 200,
    hidden: false,
    sortable: true,
    resizable: true,
    filterType: 'date',
    formatter: value => ({
      value: dayjs(value).format(DISPLAY_DATE_TIME_FORMAT),
    }),
  },
  {
    dataId: 'createdBy',
    label: i18n.t('auditList.column.createdBy'),
    minWidth: 120,
    width: 200,
    hidden: false,
    sortable: true,
    resizable: true,
    filterType: 'string',
    formatter: (value: APIUser) => ({
      value: value?.firstName + ' ' + value?.lastName,
    }),
  },
];
